/* eslint-disable max-len */
import React from 'react'
import { Icon } from '../../../../../atoms/Icon'
import { InfoTooltip } from '../../../../../InfoTooltip'
import { TooltipMetricExplanation } from '../../../../../TooltipMetricExplanation'
import {
  Card,
  CardHeader,
  CardTitle,
  MetricGrid,
  MetricRow,
  MetricLabel,
  MetricValue,
} from '../../../BuildingCharacteristics.styles'
import { translateGwrValue } from '../../../../../../utils/gwrTranslations'
import type { QuartierProfileCardProps } from '../NeighborhoodContextTab.types'

export const QuartierProfileCard: React.FC<QuartierProfileCardProps> = ({
  neighborhoodAnalysis,
  t,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Icon name="info" size="small" />
          {t('neighborhood.cards.quartierProfile.title')}
        </CardTitle>
      </CardHeader>
      <MetricGrid>
        <MetricRow>
          <MetricLabel>
            {t('neighborhood.profile.description.label')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t('neighborhood.profile.description.tooltip.title')}
                shortDescription={t(
                  'neighborhood.profile.description.tooltip.description'
                )}
                calculation={t(
                  'neighborhood.profile.description.tooltip.calculation'
                )}
                importance={t(
                  'neighborhood.profile.description.tooltip.importance'
                )}
                source={t('neighborhood.profile.description.source')}
              />
            </InfoTooltip>
          </MetricLabel>
          <MetricValue>
            {(() => {
              const desc =
                neighborhoodAnalysis.quartier_analysis.quartier_profile
                  .description
              if (!desc) return 'N/A'
              // Description format: "1960s immeuble à trois logements et plus"
              // Split at first space after decade pattern and translate the building class
              const match = desc.match(/^(\d{4}s?\s+)(.+)$/)
              if (match) {
                return `${match[1]}${translateGwrValue(match[2], 'buildingClass', t)}`
              }
              return desc
            })()}
          </MetricValue>
        </MetricRow>
        <MetricRow>
          <MetricLabel>
            {t('neighborhood.profile.typicalYear.label')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t('neighborhood.profile.typicalYear.tooltip.title')}
                shortDescription={t(
                  'neighborhood.profile.typicalYear.tooltip.description'
                )}
                calculation={t(
                  'neighborhood.profile.typicalYear.tooltip.calculation'
                )}
                importance={t(
                  'neighborhood.profile.typicalYear.tooltip.importance'
                )}
                source={t('neighborhood.profile.typicalYear.source')}
              />
            </InfoTooltip>
          </MetricLabel>
          <MetricValue>
            {
              neighborhoodAnalysis.quartier_analysis.quartier_profile
                .typical_building_year
            }
          </MetricValue>
        </MetricRow>
        <MetricRow>
          <MetricLabel>
            {t('neighborhood.profile.typicalHeight.label')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t('neighborhood.profile.typicalHeight.tooltip.title')}
                shortDescription={t(
                  'neighborhood.profile.typicalHeight.tooltip.description'
                )}
                calculation={t(
                  'neighborhood.profile.typicalHeight.tooltip.calculation'
                )}
                importance={t(
                  'neighborhood.profile.typicalHeight.tooltip.importance'
                )}
                source={t('neighborhood.profile.typicalHeight.source')}
              />
            </InfoTooltip>
          </MetricLabel>
          <MetricValue>
            {neighborhoodAnalysis.quartier_analysis.quartier_profile.typical_height.toFixed(
              1
            )}{' '}
            m
          </MetricValue>
        </MetricRow>
        <MetricRow>
          <MetricLabel>
            {t('neighborhood.profile.typicalArea.label')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t('neighborhood.profile.typicalArea.tooltip.title')}
                shortDescription={t(
                  'neighborhood.profile.typicalArea.tooltip.description'
                )}
                calculation={t(
                  'neighborhood.profile.typicalArea.tooltip.calculation'
                )}
                importance={t(
                  'neighborhood.profile.typicalArea.tooltip.importance'
                )}
                source={t('neighborhood.profile.typicalArea.source')}
              />
            </InfoTooltip>
          </MetricLabel>
          <MetricValue>
            {Math.round(
              neighborhoodAnalysis.quartier_analysis.quartier_profile
                .typical_area
            )}{' '}
            m²
          </MetricValue>
        </MetricRow>
        <MetricRow>
          <MetricLabel>
            {t('neighborhood.profile.typicalCategory.label')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t('neighborhood.profile.typicalCategory.tooltip.title')}
                shortDescription={t(
                  'neighborhood.profile.typicalCategory.tooltip.description'
                )}
                calculation={t(
                  'neighborhood.profile.typicalCategory.tooltip.calculation'
                )}
                importance={t(
                  'neighborhood.profile.typicalCategory.tooltip.importance'
                )}
                source={t('neighborhood.profile.typicalCategory.source')}
              />
            </InfoTooltip>
          </MetricLabel>
          <MetricValue>
            {translateGwrValue(
              neighborhoodAnalysis.quartier_analysis.quartier_profile
                .typical_category,
              'buildingClass',
              t
            )}
          </MetricValue>
        </MetricRow>
        <MetricRow>
          <MetricLabel>
            {t('neighborhood.profile.developmentPattern.label')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t(
                  'neighborhood.profile.developmentPattern.tooltip.title'
                )}
                shortDescription={t(
                  'neighborhood.profile.developmentPattern.tooltip.description'
                )}
                categories={[
                  {
                    label: t(
                      'neighborhood.profile.developmentPattern.categories.planned.label'
                    ),
                    description: t(
                      'neighborhood.profile.developmentPattern.categories.planned.description'
                    ),
                  },
                  {
                    label: t(
                      'neighborhood.profile.developmentPattern.categories.mixed.label'
                    ),
                    description: t(
                      'neighborhood.profile.developmentPattern.categories.mixed.description'
                    ),
                  },
                  {
                    label: t(
                      'neighborhood.profile.developmentPattern.categories.organic.label'
                    ),
                    description: t(
                      'neighborhood.profile.developmentPattern.categories.organic.description'
                    ),
                  },
                ]}
                importance={t(
                  'neighborhood.profile.developmentPattern.tooltip.importance'
                )}
                source={t('neighborhood.profile.developmentPattern.source')}
              />
            </InfoTooltip>
          </MetricLabel>
          <MetricValue>
            {t(
              `neighborhood.profile.developmentPattern.categories.${neighborhoodAnalysis.quartier_analysis.quartier_profile.development_pattern}.label`,
              {
                defaultValue:
                  neighborhoodAnalysis.quartier_analysis.quartier_profile
                    .development_pattern,
              }
            )}
          </MetricValue>
        </MetricRow>
      </MetricGrid>
    </Card>
  )
}
